import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { SpecialDate } from '@/types/specialDate';
import { specialDatesApi } from '@/services/specialDatesApi';
import toast from 'react-hot-toast';

// Predefined emoji list for special dates
const EMOJI_OPTIONS = [
  { emoji: '🎆', name: 'Yeni Yıl' },
  { emoji: '📰', name: 'Dünya Gazeteciler Günü' },
  { emoji: '❤️', name: 'Sevgililer Günü' },
  { emoji: '🌹', name: 'Dünya Kadınlar Günü' },
  { emoji: '⚕️', name: 'Tıp Bayramı' },
  { emoji: '⚖️', name: 'Avukatlar Günü' },
  { emoji: '🏥', name: 'Dünya Sağlık Günü' },
  { emoji: '🧒', name: 'Ulusal Egemenlik ve Çocuk Bayramı' },
  { emoji: '✊', name: 'Emek ve Dayanışma Günü' },
  { emoji: '💐', name: 'Anneler Günü' },
  { emoji: '🩺', name: 'Hemşireler Günü' },
  { emoji: '💊', name: 'Eczacılık Günü' },
  { emoji: '🇹🇷', name: 'Atatürk\'ü Anma, Gençlik ve Spor Bayramı' },
  { emoji: '🌍', name: 'Dünya Çevre Günü' },
  { emoji: '🥗', name: 'Dünya Diyetisyenler Günü' },
  { emoji: '🌿', name: 'Çevre Koruma Haftası' },
  { emoji: '👔', name: 'Babalar Günü' },
  { emoji: '⚓', name: 'Kabotaj ve Deniz Bayramı' },
  { emoji: '🗞️', name: 'Gazeteciler ve Basın Bayramı' },
  { emoji: '🌙', name: 'Ramazan Bayramı' },
  { emoji: '🐑', name: 'Kurban Bayramı' },
  { emoji: '🏆', name: 'Zafer Bayramı' },
  { emoji: '🕊️', name: 'Dünya Barış Günü' },
  { emoji: '✈️', name: 'Dünya Turizm Günü' },
  { emoji: '👶', name: 'Dünya Çocuk Günü' },
  { emoji: '🐾', name: 'Hayvanları Koruma Günü' },
  { emoji: '🎂', name: 'Doğum Günü' },
  { emoji: '💒', name: 'Evlilik / Nişan' },
  { emoji: '🎊', name: 'Kutlama' },
];

export const SpecialDateForm: React.FC = () => {
  const navigate = useNavigate();
  const { specialDateId } = useParams();
  const isEditMode = !!specialDateId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    date: '02-14', // MM-DD format
    emoji: '❤️',
    daysBeforeDisplay: 7,
    recommendedProducts: [] as string[],
    isActive: true,
  });

  useEffect(() => {
    if (isEditMode) {
      loadSpecialDate();
    }
  }, [specialDateId]);

  const loadSpecialDate = async () => {
    try {
      setLoading(true);
      if (!specialDateId) return;
      const data = await specialDatesApi.getSpecialDate(specialDateId);
      setFormData({
        name: data.name,
        description: data.description || '',
        date: data.date, // Already in MM-DD format
        emoji: data.emoji,
        daysBeforeDisplay: data.daysBeforeDisplay,
        recommendedProducts: data.recommendedProducts || [],
        isActive: data.isActive,
      });
    } catch (error: any) {
      console.error('Özel tarih yüklenirken hata:', error);
      toast.error(error.message || 'Özel tarih yüklenemedi');
      navigate('/admin/special-dates');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const finalValue = (type === 'checkbox') ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseInt(value) || 0,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validasyon
    const errors: string[] = [];
    if (!formData.name.trim()) errors.push('Ad zorunludur');
    if (!formData.date) errors.push('Tarih zorunludur (MM-DD)');
    if (!formData.emoji) errors.push('Emoji zorunludur');
    if (formData.daysBeforeDisplay < 0) errors.push('Gün sayısı negatif olamaz');

    // Validate date format MM-DD
    const dateRegex = /^(01|02|03|04|05|06|07|08|09|10|11|12)-(0[1-9]|[12][0-9]|3[01])$/;
    if (!dateRegex.test(formData.date)) {
      errors.push('Tarih formatı MM-DD olmalıdır (örn: 02-14)');
    }

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
      return;
    }

    try {
      setSaving(true);

      const dataToSubmit = {
        ...formData,
      };

      if (isEditMode && specialDateId) {
        await specialDatesApi.updateSpecialDate(specialDateId, dataToSubmit);
        toast.success('✅ Özel tarih güncellendi!');
      } else {
        await specialDatesApi.createSpecialDate(dataToSubmit);
        toast.success('✅ Özel tarih oluşturuldu!');
      }
      navigate('/admin/special-dates');
    } catch (error: any) {
      toast.error(error.message || 'İşlem başarısız oldu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/special-dates')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
        >
          <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode ? '✏️ Özel Tarihi Düzenle' : '📝 Yeni Özel Tarih Oluştur'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 space-y-6">
        {/* Adı */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Adı *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Örn: Sevgililer Günü"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Açıklama */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Açıklama
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Özel gün açıklaması..."
            rows={3}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Tarih (MM-DD) ve Emoji */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Tarih (MM-DD) *
            </label>
            <input
              type="text"
              name="date"
              value={formData.date}
              onChange={handleChange}
              placeholder="02-14"
              maxLength={5}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Ay-Gün formatında girin (örn: 02-14 = 14 Şubat)</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Emoji *
            </label>
            <select
              name="emoji"
              value={formData.emoji}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {EMOJI_OPTIONS.map((option) => (
                <option key={option.emoji} value={option.emoji}>
                  {option.emoji} {option.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Kaç Gün Öncesinden Gösterilsin */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Kaç Gün Öncesinden Gösterilsin? *
          </label>
          <input
            type="number"
            name="daysBeforeDisplay"
            value={formData.daysBeforeDisplay}
            onChange={handleNumericChange}
            min="0"
            max="365"
            step="1"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Örn: 7 giriş yapılırsa, 7 Şubat'tan itibaren 14 Şubat için gösterilir</p>
        </div>

        {/* Aktif */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            Aktif
          </label>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/special-dates')}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition"
          >
            <Save size={18} />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SpecialDateForm;
